import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Filter, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Combobox } from '../components/ui/combobox';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { productsAPI, makesAPI, modelsAPI, categoriesAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { useSEO } from '../hooks/useSEO';

const PER_PAGE = 24;

const FilterChip = ({ label, value, onRemove }) => (
  <span
    className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium border border-blue-200"
    data-testid={`filter-chip-${label.toLowerCase()}`}
  >
    <span className="text-blue-500">{label}:</span> {value}
    <button onClick={onRemove} aria-label={`Remove ${label} filter`} className="hover:bg-blue-100 rounded-full p-0.5">
      <X className="h-3 w-3" />
    </button>
  </span>
);

const ProductsPage = () => {
  useSEO({
    title: 'Browse All Tuning Modules',
    description: 'Find the right BlueSpark tuning module for your car. Filter by make, model, and engine. Petrol and diesel options with free Australian shipping.',
    canonical: '/products',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart } = useCart();

  // Lookup data
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [categories, setCategories] = useState([]);

  // Active filter state (derived from URL — URL is source of truth)
  const make = searchParams.get('make') || 'all';
  const model = searchParams.get('model') || 'all';
  const cc = searchParams.get('cc') || 'all';
  const category = searchParams.get('category') || 'all';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || 'name';
  const page = parseInt(searchParams.get('page') || '1', 10);

  // Local search input (debounced submission)
  const [searchInput, setSearchInput] = useState(search);

  // Results
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  // Load makes + categories once
  useEffect(() => {
    Promise.all([makesAPI.getAll(), categoriesAPI.getAll()])
      .then(([m, c]) => { setMakes(m); setCategories(c); })
      .catch((e) => console.error(e));
  }, []);

  // Load models when make changes; if no make selected, load all 720 models
  useEffect(() => {
    modelsAPI.getAll(make === 'all' ? undefined : make)
      .then(setModels)
      .catch(() => setModels([]));
  }, [make]);

  // Sync local search input when URL changes externally (e.g. from header search)
  useEffect(() => { setSearchInput(search); }, [search]);

  // Fetch products whenever any filter changes
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PER_PAGE, sort };
      if (make !== 'all') params.make = make;
      if (model !== 'all') params.model = model;
      if (cc !== 'all') params.cc = cc;
      if (category !== 'all') params.category = category;
      if (search) params.search = search;

      const data = await productsAPI.getAll(params);
      setProducts(data.products);
      setTotal(data.total);
      setPages(data.pages);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [make, model, cc, category, search, sort, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Helpers to update URL params (immutable — preserves other params)
  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    // Reset page when filters change (except when changing page itself)
    if (key !== 'page') next.delete('page');
    // Reset model when make changes
    if (key === 'make') next.delete('model');
    setSearchParams(next, { replace: true });
  };

  const clearAllFilters = () => {
    setSearchParams({}, { replace: true });
    setSearchInput('');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    updateParam('search', searchInput.trim());
  };

  const goToPage = (n) => {
    if (n < 1 || n > pages) return;
    updateParam('page', n === 1 ? null : String(n));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilters = make !== 'all' || model !== 'all' || cc !== 'all' || category !== 'all' || !!search;
  const categoryName = (id) => categories.find((c) => c.id === id)?.name || id;
  const ccLabel = (v) => {
    const n = parseInt(v, 10);
    if (!n) return v;
    return n >= 1000 ? `${(n / 1000).toFixed(1)}L (${n}cc)` : `${n}cc`;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1">
        <div className="bg-gray-100 py-8">
          <div className="container mx-auto px-4">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Tuning Products</h1>
            <p className="text-gray-600">Browse our complete range of performance tuning modules</p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          {/* Filter Bar */}
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md mb-6 space-y-4">
            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by vehicle, engine code, SKU…"
                  className="pl-9"
                  data-testid="products-search-input"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => { setSearchInput(''); updateParam('search', ''); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button type="submit" data-testid="products-search-btn">Search</Button>
            </form>

            {/* Filter Selects */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center text-sm font-medium text-gray-700 mr-2">
                <Filter className="h-4 w-4 mr-1" /> Filters:
              </div>
              <Combobox
                value={make}
                onChange={(v) => updateParam('make', v)}
                options={makes}
                placeholder="Make"
                allLabel="All Makes"
                className="w-[160px]"
                testId="filter-make"
              />

              <Combobox
                value={model}
                onChange={(v) => updateParam('model', v)}
                options={models}
                placeholder="Model"
                allLabel="All Models"
                className="w-[200px]"
                testId="filter-model"
              />

              <Select value={category} onValueChange={(v) => updateParam('category', v)}>
                <SelectTrigger className="w-[180px]" data-testid="filter-category">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>

              <div className="md:ml-auto">
                <Select value={sort} onValueChange={(v) => updateParam('sort', v === 'name' ? null : v)}>
                  <SelectTrigger className="w-[180px]" data-testid="filter-sort">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Sort: A → Z</SelectItem>
                    <SelectItem value="price_asc">Price: Low to High</SelectItem>
                    <SelectItem value="price_desc">Price: High to Low</SelectItem>
                    <SelectItem value="power_desc">Highest Power Gain</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filter Chips */}
            {hasActiveFilters && (
              <div className="flex items-center gap-2 flex-wrap pt-2 border-t">
                <span className="text-xs text-gray-500">Active:</span>
                {search && <FilterChip label="Search" value={search} onRemove={() => updateParam('search', '')} />}
                {make !== 'all' && <FilterChip label="Make" value={make} onRemove={() => updateParam('make', 'all')} />}
                {model !== 'all' && <FilterChip label="Model" value={model} onRemove={() => updateParam('model', 'all')} />}
                {cc !== 'all' && <FilterChip label="Engine" value={ccLabel(cc)} onRemove={() => updateParam('cc', 'all')} />}
                {category !== 'all' && <FilterChip label="Category" value={categoryName(category)} onRemove={() => updateParam('category', 'all')} />}
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-blue-600 hover:underline ml-2"
                  data-testid="clear-all-filters"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Result count + pagination summary */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <p className="text-gray-600 text-sm">
              {loading ? 'Loading…' : (
                total === 0
                  ? 'No products found'
                  : `Showing ${(page - 1) * PER_PAGE + 1}–${Math.min(page * PER_PAGE, total)} of ${total.toLocaleString()} product${total === 1 ? '' : 's'}`
              )}
            </p>
            {pages > 1 && (
              <p className="text-sm text-gray-500">Page {page} of {pages}</p>
            )}
          </div>

          {/* Results */}
          {loading ? (
            <div className="text-center py-16 text-gray-500">Loading products…</div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg border">
              <p className="text-xl text-gray-600 mb-2">No products match your filters</p>
              <p className="text-sm text-gray-500 mb-4">
                Try removing one of the filters or searching for a different vehicle.
              </p>
              <Button onClick={clearAllFilters} data-testid="empty-clear-filters">Clear all filters</Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <Card key={product.id} className="flex flex-col hover:shadow-xl transition-shadow" data-testid={`product-card-${product.id}`}>
                    <CardHeader>
                      <div className="aspect-square bg-gray-50 rounded-lg flex items-center justify-center mb-4">
                        <img
                          src={product.image}
                          alt={`${product.name} — ${product.make} ${product.model} tuning module`}
                          width={480}
                          height={480}
                          className="w-full h-full object-contain p-4"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <h3 className="font-bold text-lg line-clamp-2">{product.name}</h3>
                    </CardHeader>
                    <CardContent className="flex-1">
                      <div className="space-y-2 text-sm">
                        <p className="text-gray-600">
                          <span className="font-semibold">Vehicle:</span> {product.make} {product.model}
                        </p>
                        {product.engine && (
                          <p className="text-gray-600">
                            <span className="font-semibold">Engine:</span> {product.engine}
                          </p>
                        )}
                        <div className="flex items-center justify-between bg-blue-50 p-3 rounded">
                          <div>
                            <p className="text-xs text-gray-600">Power Gain</p>
                            <p className="font-bold text-blue-600">+{product.gainPower}PS</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Torque Gain</p>
                            <p className="font-bold text-blue-600">+{product.gainTorque}Nm</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex flex-col space-y-3">
                      <div className="flex items-center justify-between w-full">
                        <span className="text-2xl font-bold text-blue-600">
                          ${product.price.toFixed(2)} {product.currency}
                        </span>
                        {product.inStock ? (
                          <span className="text-sm text-green-600 font-medium">In Stock</span>
                        ) : (
                          <span className="text-sm text-red-600 font-medium">Out of Stock</span>
                        )}
                      </div>
                      <div className="flex gap-2 w-full">
                        <Link to={`/product/${product.id}`} className="flex-1">
                          <Button variant="outline" className="w-full">
                            View Details
                          </Button>
                        </Link>
                        <Button
                          onClick={() => addToCart(product)}
                          disabled={!product.inStock}
                          className="flex-1"
                        >
                          Add to Cart
                        </Button>
                      </div>
                    </CardFooter>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10" data-testid="pagination">
                  <Button variant="outline" size="sm" onClick={() => goToPage(page - 1)} disabled={page <= 1} data-testid="page-prev">
                    <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                  </Button>
                  {Array.from({ length: Math.min(7, pages) }, (_, i) => {
                    let p;
                    if (pages <= 7) p = i + 1;
                    else if (page <= 4) p = i + 1;
                    else if (page >= pages - 3) p = pages - 6 + i;
                    else p = page - 3 + i;
                    return (
                      <Button
                        key={p}
                        variant={p === page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => goToPage(p)}
                        data-testid={`page-${p}`}
                      >
                        {p}
                      </Button>
                    );
                  })}
                  <Button variant="outline" size="sm" onClick={() => goToPage(page + 1)} disabled={page >= pages} data-testid="page-next">
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductsPage;
